import React, { FC, useContext, useEffect, useState } from "react";
import { NO_IMAGE } from "../../../../assets";
import { HeaderSection } from "../../../../models/manageWebSite";
import { MobXProviderContext } from "mobx-react";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { FileUpload } from "primereact/fileupload";
import { getErrorTitle, WEBSITE_TEMPLATE_MAX_IMAGE_FILE_SIZE_IN_BYTES } from "../../../../constants/constants";
import { Button } from "primereact/button";
import { Controller, useForm } from "react-hook-form";
import { ReferenceType } from "../../../../models/document";
import { InputText } from "primereact/inputtext";
import { normalizePlainText } from "../../../../utils/securityUtils";

interface EditModalProps {
  data: HeaderSection | null;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  homeId: string | null;
}

const EditModal: FC<EditModalProps> = ({
  isOpen,
  toggleModal,
  flag,
  setFlag,
  data,
  homeId,
}) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loadingImg, setLoadingImg] = useState<boolean>(false);
  const [updatedData, setUpdatedData] = useState<HeaderSection | null>(data);

  const defaultValues = {
    title: data?.title || "",
  }

  const { formState: { errors }, handleSubmit, reset, control } = useForm({ defaultValues });

  useEffect(() => {
    setUpdatedData(data);
    setImageUrl(data?.logoSignedImg || null);
    reset({
      title: data?.title || "",
    });
  }, [data, isOpen]);

  const handleFileChange = (event) => {
    if (event.files && event.files[0]) {
      const file = event.files[0];
      setSelectedFile(file);
      const url = URL.createObjectURL(file)
      setImageUrl(url);
      setUpdatedData((prev) =>
        prev
          ? { ...prev, logo: `website/default_template/${homeId}/draft/HEADER-logo` }
          : prev,
      );
    }
  };

  const uploadProfileImage = async (objectKey: string) => {
    if (!imageUrl) return null;
    try {
      const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(
        homeId!,
        ReferenceType.BUSINESS_HOME,
        objectKey,
      );
      await backendAPIStore.uploadFileToS3(presignedUrl, selectedFile);
      return presignedUrl.split("?")[0];
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      return null;
    }
  };

  const onSubmit = async (data: any) => {
    if (!imageUrl) {
      setErrorMessage("Please select an image before submitting.");
      return;
    }
    setErrorMessage(null);
    setLoadingImg(true);
    const objectKey = `website/default_template/${homeId}/draft/HEADER-logo`
    try {
      await backendAPIStore.init();

      let { logoSignedImg, ...apiData } = {
        ...updatedData,
        modifiedAt: new Date().toISOString(),
        title: normalizePlainText(data.title, { maxLength: 120 }),
      };

      if (selectedFile) {
        await uploadProfileImage(objectKey);
        let { logoSignedImg, ...apiData } = {
          ...updatedData,
          logo: objectKey,
        };
      }

      await backendAPIStore.updateTemplateSection(apiData);
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: 'Failed to upload image', life: 3000 });
    } finally {
      reset();
      toggleModal(false);
      setFlag(!flag);
      setSelectedFile(null);
      setLoadingImg(false);
    }
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const headerElement = (
    <div className="inline-flex align-items-center justify-content-center gap-2">
      <span className="font-bold white-space-nowrap">Upload new Logo</span>
    </div>
  );

  const footerContent = (
    <div>
      <div className="p-error">{errorMessage}</div>
      <Button type="submit" label="Save" severity="warning" rounded className="mt-2 p-button-outlined" loading={loadingImg} onClick={handleSubmit(onSubmit)} />
    </div>
  );

  const chooseOptions = { icon: 'pi pi-fw pi-images', className: 'custom-choose-btn p-button-rounded p-button-outlined' };
  const cancelOptions = { icon: 'pi pi-fw pi-times', className: 'custom-cancel-btn p-button-rounded p-button-outlined p-button-danger' };

  const fileHeaderTemplate = (options) => {
    const { className, chooseButton, cancelButton } = options;

    return (
      <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
        {chooseButton}
        {cancelButton}
      </div>
    );
  };

  return (
    <Dialog visible={isOpen} modal header={headerElement} footer={footerContent} style={{ width: '50rem' }}
      onHide={() => { if (!isOpen) return; toggleModal(false); }}>
      <Toast ref={toast} />
      <form>
        <div className="field m-2">
          <label htmlFor="title" className="block mb-2 font-medium">Business Text</label>
          <Controller
            name="title"
            control={control}
            rules={{
              required: "Business text is required.",
              maxLength: { value: 120, message: "Business text must be at most 120 characters long." },
            }}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                className={fieldState.invalid ? "p-invalid w-full" : "w-full"}
                placeholder="Professional adult family home care"
              />
            )}
          />
          {getFormErrorMessage("title")}
        </div>
        <div className="card m-2 justify-content-center align-items-center grid">
          <img src={imageUrl || NO_IMAGE} width="200" height="200" style={{ objectFit: "contain" }} className="col" />
          <FileUpload mode="advanced" name="profileImage" onSelect={handleFileChange} accept="image/*"
            maxFileSize={WEBSITE_TEMPLATE_MAX_IMAGE_FILE_SIZE_IN_BYTES}
            headerTemplate={fileHeaderTemplate} className="col"
            chooseOptions={chooseOptions} cancelOptions={cancelOptions}
            emptyTemplate={<p className="m-0">Drag and drop image here to upload.</p>}
          />
        </div>
      </form>
    </Dialog>
  );
};

export default EditModal;
